import {
    useEffect
} from "react";
import {
    type ViewStyle,
    Platform
} from "react-native";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

export const WEB_SCROLLBAR_TRACK_RADIUS_VARIABLE = "--ncore-scrollbar-track-radius";

export const getWebScrollbarEdgeStyle = (radius: number): ViewStyle => {
    if(Platform.OS !== "web") {
        return {};
    }

    return {
        [WEB_SCROLLBAR_TRACK_RADIUS_VARIABLE]: `0px 0px ${radius}px ${radius}px`
    } as unknown as ViewStyle;
};

const WebScrollbar = () => {
    const {
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        if(Platform.OS !== "web") {
            return;
        }

        const doc = globalThis.document as Document | undefined;

        if(!doc || !doc.head) {
            return;
        }

        const styleId = "ncore-web-scrollbar-styles";

        let styleEl = doc.getElementById(styleId) as HTMLStyleElement | null;

        if(!styleEl) {
            styleEl = doc.createElement("style");
            styleEl.id = styleId;
            styleEl.type = "text/css";
            doc.head.appendChild(styleEl);
        }

        const gutter = spaces.spacingXs;

        const getGutterRules = (selector: string, border: number) => `
                #root *::-webkit-scrollbar-${selector}:vertical,
                body *::-webkit-scrollbar-${selector}:vertical,
                html *::-webkit-scrollbar-${selector}:vertical {
                    border-left-width: ${border + gutter}px !important;
                }

                [dir="rtl"] *::-webkit-scrollbar-${selector}:vertical {
                    border-left-width: ${border}px !important;
                    border-right-width: ${border + gutter}px !important;
                }

                #root *::-webkit-scrollbar-${selector}:horizontal,
                body *::-webkit-scrollbar-${selector}:horizontal,
                html *::-webkit-scrollbar-${selector}:horizontal {
                    border-top-width: ${border + gutter}px !important;
                }
        `;

        styleEl.innerHTML = `
            @media (hover: hover) and (pointer: fine) {
                #root *::-webkit-scrollbar,
                body *::-webkit-scrollbar,
                html *::-webkit-scrollbar {
                    height: ${shapeConfigs.scrollBarHeight + gutter}px !important;
                    width: ${shapeConfigs.scrollBarWidth + gutter}px !important;
                }

                #root *::-webkit-scrollbar-track,
                body *::-webkit-scrollbar-track,
                html *::-webkit-scrollbar-track {
                    border: ${borders.scrollBarTrack}px solid transparent !important;
                    background: ${colors.content.container.subtle} !important;
                    border-radius: ${radiuses.scrollBarTrack}px !important;
                    background-clip: padding-box !important;
                }

                #root *::-webkit-scrollbar-track:horizontal,
                body *::-webkit-scrollbar-track:horizontal,
                html *::-webkit-scrollbar-track:horizontal {
                    border-radius: var(${WEB_SCROLLBAR_TRACK_RADIUS_VARIABLE}, ${radiuses.scrollBarTrack}px) !important;
                }

                #root *::-webkit-scrollbar-thumb,
                body *::-webkit-scrollbar-thumb,
                html *::-webkit-scrollbar-thumb {
                    border: ${borders.scrollBarThumb}px solid transparent !important;
                    background: ${colors.content.text.contrastLow} !important;
                    border-radius: ${radiuses.scrollBarThumb}px !important;
                    background-clip: padding-box !important;
                }

                #root *::-webkit-scrollbar-thumb:hover,
                body *::-webkit-scrollbar-thumb:hover,
                html *::-webkit-scrollbar-thumb:hover {
                    border: ${borders.scrollBarThumbHover}px solid transparent !important;
                    border-radius: ${radiuses.scrollBarThumbHover}px !important;
                    background: ${colors.content.icon.disabled} !important;
                    background-clip: padding-box !important;
                }

                #root *::-webkit-scrollbar-thumb:active,
                body *::-webkit-scrollbar-thumb:active,
                html *::-webkit-scrollbar-thumb:active {
                    border: ${borders.scrollBarThumbActive}px solid transparent !important;
                    border-radius: ${radiuses.scrollBarThumbActive}px !important;
                    background: ${colors.content.icon.low} !important;
                    background-clip: padding-box !important;
                }
                ${getGutterRules("track", borders.scrollBarTrack)}
                ${getGutterRules("thumb", borders.scrollBarThumb)}
                ${getGutterRules("thumb:hover", borders.scrollBarThumbHover)}
                ${getGutterRules("thumb:active", borders.scrollBarThumbActive)}
            }
        `;

        return () => {
        };
    }, [
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    ]);

    return null;
};
export default WebScrollbar;
